import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import { useTheme } from '@mui/material/styles';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { WIDGET_REGISTRY } from "./widgetRegistry";
import { TC } from "../../tokens/text";

export default function AddWidgetDialog({ open, onClose, onAdd, activeWidgetTypes = [] }) {
  const theme = useTheme();
  // Keep white text in dark mode (previous behaviour), otherwise use text.primary for light mode
  const forcedContrast = theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary;

  const handleWidgetSelect = (type) => {
    onAdd(type);
    onClose();
  };

  const handleWidgetKeyDown = (event, type) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleWidgetSelect(type);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-widget-dialog-title"
      aria-describedby="add-widget-dialog-description"
      PaperProps={{
        sx: {
          background: theme.palette.background.paper,
          color: forcedContrast,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "16px",
          boxShadow: theme.shadows[24],
        }
      }}
    >
      <DialogTitle
        id="add-widget-dialog-title"
        sx={(theme) => ({ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, background: theme.palette.background.paper })}
      >
        <VuiTypography variant="h6" fontWeight="bold" style={{ color: forcedContrast, fontSize: '1.05rem' }}>
          Add Widget
        </VuiTypography>
        <IconButton
          onClick={onClose}
          aria-label="Close add widget dialog"
          size="small"
          sx={{ color: forcedContrast, background: 'transparent', "&:hover": { color: theme.palette.primary.contrastText, background: theme.palette.action.hover } }}
        >
          <span aria-hidden="true" style={{ fontSize: "1.2rem", lineHeight: 1, color: forcedContrast }}>×</span>
        </IconButton>
      </DialogTitle>
      <DialogContent id="add-widget-dialog-description" sx={{ pt: 1, pb: 3 }}>
        <VuiBox display="flex" flexDirection="column" gap={1.5}>
          {Object.entries(WIDGET_REGISTRY).map(([type, cfg]) => {
            const alreadyAdded = activeWidgetTypes.includes(type);
            return (
              <VuiBox
                key={type}
                onClick={() => handleWidgetSelect(type)}
                onKeyDown={(event) => handleWidgetKeyDown(event, type)}
                role="button"
                tabIndex={0}
                display="flex"
                alignItems="center"
                gap={2}
                p={2}
              sx={(theme) => ({
                background: alreadyAdded ? theme.palette.action.selected : theme.palette.background.paper,
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: "12px",
                cursor: "pointer",
                opacity: alreadyAdded ? 0.95 : 1,
                "&:hover": { background: theme.palette.action.hover, border: `2px solid ${theme.palette.primary.main}33` },
                transition: "all 0.12s",
                // visible focus ring for keyboard users
                '&:focus': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '3px' }
              })}
              >
              <VuiTypography style={{ fontSize: '1.6rem', lineHeight: 1, color: forcedContrast }} aria-hidden>{cfg.icon}</VuiTypography>
                <VuiBox flex={1}>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <VuiTypography style={{ color: forcedContrast, fontWeight: 700, fontSize: '1rem' }}>
                      {cfg.label}
                    </VuiTypography>
                    {alreadyAdded && (
                    <VuiTypography style={{ fontSize: '0.78rem', color: forcedContrast, marginLeft: 8, opacity: 0.95 }}>
                        (already added)
                      </VuiTypography>
                    )}
                  </VuiBox>
                <VuiTypography style={{ color: forcedContrast, fontSize: '0.9rem', marginTop: 6 }}>
                    {cfg.description}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            );
          })}
        </VuiBox>
      </DialogContent>
    </Dialog>
  );
}

